import type { PaymentResponse } from '@monorepo/shared/schemas/payments'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertCircle,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Receipt,
  XCircle,
  Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import type { Customer } from '@/features/customers/services/customerService'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import {
  AddPaymentDialog,
  PaymentDetailsDialog,
  paymentsService,
} from '@/features/payments'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

interface PaymentsTabProps {
  customer: Customer
}

export function PaymentsTab({ customer }: PaymentsTabProps) {
  const navigate = useNavigate()
  const { currentEnvironment } = useEnvironment()
  const [payments, setPayments] = useState<PaymentResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPayment, setSelectedPayment] =
    useState<PaymentResponse | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  useEffect(() => {
    const loadPayments = async () => {
      setLoading(true)
      try {
        const result = await paymentsService.listPayments({
          customer_id: customer.id,
        })
        setPayments(result.data)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      } finally {
        setLoading(false)
      }
    }

    loadPayments()
  }, [customer.id, currentEnvironment?.id])

  const getStatusVariant = (status: PaymentResponse['status']) => {
    switch (status) {
      case 'succeeded':
        return 'default'
      case 'pending':
        return 'secondary'
      case 'failed':
        return 'destructive'
      case 'refunded':
        return 'outline'
      case 'canceled':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const getStatusIcon = (status: PaymentResponse['status']) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className='w-4 h-4' />
      case 'pending':
        return <Clock className='w-4 h-4' />
      case 'failed':
        return <XCircle className='w-4 h-4' />
      case 'refunded':
        return <AlertCircle className='w-4 h-4' />
      case 'canceled':
        return <AlertCircle className='w-4 h-4' />
      default:
        return <AlertCircle className='w-4 h-4' />
    }
  }

  const getStatusLabel = (status: PaymentResponse['status']) => {
    switch (status) {
      case 'succeeded':
        return 'Completed'
      case 'canceled':
        return 'Canceled'
      default:
        return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }

  const getPaymentTypeIcon = (type: string) => {
    switch (type) {
      case 'credit_card':
        return <CreditCard className='w-4 h-4' />
      case 'debit_card':
        return <CreditCard className='w-4 h-4' />
      case 'wired':
      case 'wire_transfer':
        return <Zap className='w-4 h-4' />
      case 'bank_account':
        return <Building className='w-4 h-4' />
      default:
        return <CreditCard className='w-4 h-4' />
    }
  }

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getTotalPayments = () => {
    return payments
      .filter(p => p.status === 'succeeded')
      .reduce((sum, p) => sum + p.amount, 0)
  }

  const getPendingAmount = () => {
    return payments
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + p.amount, 0)
  }

  // Define table columns
  const paymentColumns: ColumnDef<PaymentResponse>[] = [
    {
      accessorKey: 'payment_id',
      header: 'Payment Number',
      size: 180,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.payment_id}</div>
          <div className='text-sm text-muted-foreground'>{row.original.id}</div>
        </div>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Payment Date',
      size: 150,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Calendar className='w-4 h-4 text-muted-foreground' />
          <span className='text-sm'>
            {formatDate(row.original.processed_at || row.original.created_at)}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'payment_method',
      header: 'Payment Method',
      size: 150,
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          {getPaymentTypeIcon(row.original.payment_method ?? '')}
          <span className='text-sm capitalize'>
            {row.original.payment_method?.replace(/_/g, ' ') ?? '—'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: 120,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>
            {formatCurrency(row.original.amount, row.original.currency)}
          </div>
          {row.original.refunded_amount > 0 && (
            <div className='text-sm text-red-600'>
              -{formatCurrency(row.original.refunded_amount)} refunded
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'invoice_id',
      header: 'Invoice ID',
      size: 150,
      cell: ({ row }) => (
        <div>
          {row.original.invoice_id ? (
            <div className='flex items-center gap-1'>
              <Receipt className='w-4 h-4 text-muted-foreground' />
              <span className='text-sm font-medium'>
                {row.original.invoice_id}
              </span>
            </div>
          ) : (
            <span className='text-sm text-muted-foreground'>-</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'balance',
      header: 'Balance',
      size: 120,
      cell: ({ row }) => {
        const balance =
          row.original.amount - (row.original.refunded_amount || 0)
        return (
          <div className='font-medium'>
            {formatCurrency(balance, row.original.currency)}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => (
        <Badge
          variant={getStatusVariant(row.original.status)}
          className='gap-1'
        >
          {getStatusIcon(row.original.status)}
          {getStatusLabel(row.original.status)}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
              <MoreHorizontal className='w-4 h-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-48'>
            <DropdownMenuItem
              onClick={() => handleViewPaymentDetails(row.original)}
            >
              <Eye className='w-4 h-4 mr-2' />
              View Details
            </DropdownMenuItem>
            {row.original.invoice_id && (
              <DropdownMenuItem onClick={() => handleViewInvoice(row.original)}>
                <ExternalLink className='w-4 h-4 mr-2' />
                View Invoice
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  // Handler functions
  const handleViewPaymentDetails = (payment: PaymentResponse) => {
    setSelectedPayment(payment)
  }

  const handleViewInvoice = (payment: PaymentResponse) => {
    if (payment.invoice_id) {
      navigate({
        to: '/invoice-detail/$invoiceId' as string,
        params: { invoiceId: payment.invoice_id } as never,
      })
    }
  }

  const handlePaymentCreated = () => {
    // Reload payments to show the new one
    const loadPayments = async () => {
      try {
        const result = await paymentsService.listPayments({
          customer_id: customer.id,
        })
        setPayments(result.data)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
    loadPayments()
  }

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header Skeleton */}
        <div className='flex justify-between items-center'>
          <div className='animate-pulse'>
            <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-48 mb-2'></div>
          </div>
          <div className='animate-pulse'>
            <div className='h-10 bg-slate-300 dark:bg-slate-600 rounded w-32'></div>
          </div>
        </div>

        {/* Cards Skeleton */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          {Array.from({ length: 2 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Card key={i} className='bg-transparent border'>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <div className='animate-pulse'>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-20'></div>
                </div>
                <div className='animate-pulse'>
                  <div className='h-4 w-4 bg-slate-300 dark:bg-slate-600 rounded'></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className='animate-pulse'>
                  <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-24 mb-1'></div>
                  <div className='h-3 bg-slate-300 dark:bg-slate-600 rounded w-16'></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className='bg-transparent border'>
          <div className='animate-pulse space-y-4 p-6'>
            <div className='grid grid-cols-9 gap-4 pb-4 border-b'>
              {Array.from({ length: 9 }).map((_, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  key={i}
                  className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                ></div>
              ))}
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              <div key={i} className='grid grid-cols-9 gap-4 py-3'>
                {Array.from({ length: 9 }).map((_, j) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={j}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  ></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-2xl font-bold'>Payments</h2>
        </div>
        <AddPaymentDialog
          preselectedCustomerId={customer.id}
          onPaymentCreated={handlePaymentCreated}
        />
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Payments</span>
              <span className='text-md font-bold'>
                {formatCurrency(getTotalPayments())}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              Successfully processed
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Pending Payments</span>
              <span className='text-md font-bold'>
                {formatCurrency(getPendingAmount())}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>Processing</p>
          </CardContent>
        </Card>
      </div>

      {/* Payments Table */}
      <div className='bg-transparent border'>
        {payments.length > 0 ? (
          <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
            <EnhancedTable
              data={payments}
              columns={paymentColumns.map(col => ({
                ...col,
                enableSorting: false,
              }))}
              height='500px'
              className='bg-transparent'
            />
          </div>
        ) : (
          <Card>
            <CardContent>
              <div className='text-center py-12 text-muted-foreground'>
                <DollarSign className='w-12 h-12 mx-auto mb-4 opacity-50' />
                <h3 className='text-lg font-medium mb-2'>No payments found</h3>
                <p className='text-sm max-w-sm mx-auto mb-4'>
                  Payment history will appear here once transactions are
                  processed.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payment Details Dialog */}
      <PaymentDetailsDialog
        payment={selectedPayment}
        onClose={() => setSelectedPayment(null)}
      />
    </div>
  )
}
